import React, { FC, useEffect, useMemo, useRef } from 'react';
import { List, ListItemText, useTheme } from '@mui/material';
import { Grid } from '@mui/system';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { TAB_QUERY_PARAM } from 'pages/ValuationsAllocation/common/constants/valuations';
import { InvalidCellEntry } from 'pages/ValuationsAllocation/types';
import { formatInvalidCell } from 'pages/ValuationsAllocation/util/getInvalidTables';
import { revealSpreadsheetCell } from 'pages/ValuationsAllocation/util/revealSpreadsheetCell';
import { scrollToTop, setQueryString } from 'utilities';
import {
  CellButton,
  CellList,
  CellListItem,
  IconContainer,
  ListContainer,
  ListIconContainer,
  ListItemBox,
  ListItemStyled,
  MessageContainer,
  StyledErrorOutlineIcon,
  StyledNotificationIcon,
  Title,
} from './styles';
import { InvalidTablesMessageProps } from './types';

const TITLE = 'To proceed, please correct the highlighted errors in the table cells or invalid characters in the approach names:';
const MAX_VISIBLE_INVALID_CELLS = 5;

const InvalidTablesMessage: FC<InvalidTablesMessageProps> = props => {
  const { invalidTables = [], onRevealCell, title } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const cancelRevealRef = useRef<(() => void) | undefined>(undefined);

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);
  const { [TAB_QUERY_PARAM]: approachId } = parsedQuery;

  useEffect(() => {
    scrollToTop();
  }, [invalidTables]);

  useEffect(() => () => cancelRevealRef.current?.(), []);

  const redirectToTab = (id: string) => {
    if (approachId !== id) {
      setQueryString({
        navigate,
        paramName: TAB_QUERY_PARAM,
        paramValue: id,
        pathname: location.pathname,
        searchParams: queryString.stringify(parsedQuery),
        shouldPushPath: true,
      });
    }
  };

  const revealCell = (tableId: string, cell: InvalidCellEntry) => {
    redirectToTab(tableId);
    onRevealCell?.(cell);
    cancelRevealRef.current?.();
    cancelRevealRef.current = revealSpreadsheetCell({
      cellKey: cell.cellKey,
      flashColor: theme.palette.error.main,
      sheetName: cell.sheetName,
    });
  };

  return (
    <MessageContainer id="invalid-tables-message" sx={title ? { pb: 2.5, ml: 1.5 } : {}}>
      <Grid container alignItems="center" justifyContent="flex-start">
        <IconContainer>
          <StyledNotificationIcon />
        </IconContainer>
        <Grid>
          <Title>{title ?? TITLE}</Title>
        </Grid>
      </Grid>
      {Boolean(invalidTables?.length) && (
        <ListContainer>
          <List>
            {invalidTables.map(({ approachType, cells = [], id, name }) => (
              <ListItemStyled key={`${name}-${id}`}>
                <ListItemBox onClick={() => redirectToTab(id)}>
                  <ListIconContainer>
                    <StyledErrorOutlineIcon />
                  </ListIconContainer>
                  <ListItemText primary={name} secondary={approachType ? `(${approachType})` : null} />
                </ListItemBox>
                {cells.length > 0 && (
                  <CellList>
                    {cells.slice(0, MAX_VISIBLE_INVALID_CELLS).map(cell => (
                      <CellListItem key={`${cell.sheetName}-${cell.cellKey}`}>
                        <CellButton
                          data-testid={`invalid-cell-${cell.sheetName}-${cell.cellKey}`}
                          onClick={() => revealCell(id, cell)}>
                          {formatInvalidCell(cell)}
                        </CellButton>
                      </CellListItem>
                    ))}
                    {cells.length > MAX_VISIBLE_INVALID_CELLS && (
                      <CellListItem sx={{ px: 1 }}>{`+${cells.length - MAX_VISIBLE_INVALID_CELLS} more`}</CellListItem>
                    )}
                  </CellList>
                )}
              </ListItemStyled>
            ))}
          </List>
        </ListContainer>
      )}
    </MessageContainer>
  );
};

export default InvalidTablesMessage;
